/**
 * enginePreProcessJson_Merge_Conditional.ts - Conditional block processing for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { tryGetCanonicalKey } from '../../base/engine/htmlJsonMergeUtil.js';
import { getValue as getValueHelper, evaluateConditionWithContext } from './enginePreProcessJson_Helper.js';
import { TemplateInfoNode, TemplateOperation } from './enginePreProcessJson_Helper.js';
import { TemplateOperationKind } from './enginePreProcessJson_Helper.js';
import type { EnginePreProcessJson } from './enginePreProcessJson.js';
import type { ILoaderPreProcessJson } from './enginePreProcessJson.js';
import type { PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Merge template function signature
 */
type MergeTemplateFn = (
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    htmlJson: JsonObject,
    dataJson: JsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    flowStack: string[] | null,
    parentComponentSink?: TemplateOperation[] | null,
    keyReplaceStage?: TemplateOperation[] | null,
    keyMissingStage?: TemplateOperation[] | null,
    parentSlotSink?: TemplateOperation[] | null
) => string;

interface PairedConditionalBlocks {
    positiveContent: JsonObject | null;
    negativeContent: JsonObject | null;
}

export function findPairedConditionalBlocks(conditionalBlockData: JsonObject): PairedConditionalBlocks {
    const contentRaw = getValueHelper(conditionalBlockData, 'Content');
    const positiveContent: JsonObject | null = contentRaw instanceof JsonObject
        ? contentRaw
        : (typeof contentRaw === 'object' && contentRaw !== null ? new JsonObject(contentRaw as PlainJsonObject) : null);

    let negativeContent: JsonObject | null = null;
    const negativeBlockRaw = getValueHelper(conditionalBlockData, 'NegativeBlock');
    const negativeBlock: JsonObject | null = negativeBlockRaw instanceof JsonObject
        ? negativeBlockRaw
        : (typeof negativeBlockRaw === 'object' && negativeBlockRaw !== null ? new JsonObject(negativeBlockRaw as PlainJsonObject) : null);
    if (negativeBlock) {
        const negContentRaw = getValueHelper(negativeBlock, 'Content');
        negativeContent = negContentRaw instanceof JsonObject
            ? negContentRaw
            : (typeof negContentRaw === 'object' && negContentRaw !== null ? new JsonObject(negContentRaw as PlainJsonObject) : null);
    }

    return {
        positiveContent,
        negativeContent
    };
}

export function selectConditionalContentForCondition(
    conditionMet: boolean,
    positiveContent: JsonObject | null,
    negativeContent: JsonObject | null
): JsonObject | null {
    return conditionMet ? positiveContent : negativeContent;
}

/**
 * Processes a conditional block ({{?Key}} marker) - works both inside and outside arrays
 */
export function processConditionalBlock(
    mergeTemplate: MergeTemplateFn,
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    value: unknown,
    keyNameFromKey: string,
    dataJson: JsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    flowStack: string[] | null,
    parentComponentSink: TemplateOperation[] | null = null,
    parentSlotSink: TemplateOperation[] | null = null
): string {
    const conditionalBlockData = value instanceof JsonObject ? value : new JsonObject(value as PlainJsonObject);
    let conditionalKey = (getValueHelper(conditionalBlockData, 'ConditionalKey') as string | null) || keyNameFromKey;

    // rawCondition is the key's own truthiness. conditionMet is what selects the rendered body and
    // is inverted for negative conditionals ({{^Key}}). Operation logging must report rawCondition,
    // NOT conditionMet — Normal's handleConditional logs the condition value itself, so reporting
    // the inverted value would flip True/False against Normal for every negative block.
    const rawCondition = evaluateConditionWithContext(conditionalKey, context.contextJson, dataJson);
    let conditionMet = rawCondition;
    const isNegativeBlock = !!getValueHelper(conditionalBlockData, 'IsNegative');
    // Invert for negative conditionals ({{^Key}})
    if (isNegativeBlock) {
        conditionMet = !conditionMet;
    }

    // Normal dispatches on the DATA TYPE, not just the marker: a key whose value is an array is
    // owned by the array machinery, which logs nothing for a standalone {{^Key}} (there is no
    // {{@Key}} MainBlock to report). Only non-array values reach its conditional processor and
    // produce a ConditionalBlock op. Mirror that here — rendering is unaffected either way,
    // since array truthiness (non-empty) already drives which body is selected.
    let resolvedConditionalValue: unknown = null;
    if (conditionalKey) {
        if (context.contextJson) {
            for (const ctxKey of context.contextJson.keys()) {
                if (ctxKey.toLowerCase() === conditionalKey.toLowerCase()) {
                    resolvedConditionalValue = context.contextJson.get(ctxKey);
                    break;
                }
            }
        }
        if (resolvedConditionalValue === null && dataJson) {
            for (const dataKey of dataJson.keys()) {
                if (dataKey.toLowerCase() === conditionalKey.toLowerCase()) {
                    resolvedConditionalValue = dataJson.get(dataKey);
                    break;
                }
            }
        }
    }
    const ownedByArrayMachinery = isNegativeBlock
        && resolvedConditionalValue instanceof JsonValue
        && resolvedConditionalValue.isArray;

    // Use data-source canonical key casing for Target+ReplacedWith so JSON engines emit
    // the same identifier as Normal/PreProcess (which iterate merged data keys).
    if (conditionalKey) {
        conditionalKey = tryGetCanonicalKey(dataJson, conditionalKey);
    }

    const { positiveContent, negativeContent } = findPairedConditionalBlocks(conditionalBlockData);
    const selectedContent = selectConditionalContentForCondition(conditionMet, positiveContent, negativeContent);

    // Log ConditionalBlock operation (once per key - Normal engine logs one op per key via handleConditional)
    // BEFORE merging the body: Normal iterates the merged data keys, so an enclosing conditional is
    // logged ahead of the blocks nested inside it. Logging after the recursion below would emit the
    // parent AFTER its children (post-order) and reorder the ops trace against Normal for any nested
    // conditional (e.g. {{?ShowPasswordReset}} wrapping the ShowPasswordReset* blocks in LoginForm).
    if (operations && genOperations && !ownedByArrayMachinery) {
        const target = '{{^' + conditionalKey + '}}';
        const alreadyLogged = operations.some(op => op.kind === TemplateOperationKind.ConditionalBlock && op.target === target);
        if (!alreadyLogged) {
            const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: opTemplateName,
                kind: TemplateOperationKind.ConditionalBlock,
                target: target,
                replacedWith: `${templateInfo.templateName}_${conditionalKey} (${rawCondition ? 'True' : 'False'})`,
                operations: []
            }));
        }
    }

    let merged = '';
    if (selectedContent) {
        // Hoist slot/component ops in conditional body to OUTER template's slot/component
        // phase (matches Normal: conditional resolved in JSON-merge phase, slots then
        // components in subsequent passes over the whole content). Pass both sinks through
        // for the selected branch (positive OR negative), mirroring EngineNormalJson.
        merged = mergeTemplate(
            engine,
            loader,
            context,
            templateInfo,
            selectedContent,
            dataJson,
            operations,
            genOperations,
            componentHtml,
            recursionDepth + 1,
            flowStack,
            parentComponentSink,
            null,
            null,
            parentSlotSink
        );
    }

    return merged;
}
